import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { createReactiveContext } from '../../../../../helpers/reactiveContext';
import { useEvent } from '../../../../../hooks/useEvent';

const {
  Provider,
  useMethods,
  useSelector: useSearchParamsSelector,
} = createReactiveContext();

const Context = createContext();

const useSearchParamsMethods = () => useContext(Context);

const SearchParamsProvider = ({ children }) => (
  <Provider>
    <SearchParamsMethods>{children}</SearchParamsMethods>
  </Provider>
);

export {
  SearchParamsProvider,
  useSearchParamsMethods,
  useSearchParamsSelector,
};

function SearchParamsMethods({ children }) {
  const { reset } = useMethods();

  const history = useHistory();

  const location = useLocation();

  const setParams = useEvent((values) => {
    if (history.location.pathname !== location.pathname) {
      return;
    }

    const currentLocation = history.location;
    const params = new URLSearchParams(currentLocation.search);

    Object.entries(values).forEach(([key, value]) => {
      _set(params, key, value);
    });

    history.replace({ ...currentLocation, search: params.toString() });
  });

  const getParams = useEvent((names) => {
    const params = new URLSearchParams(history.location.search);

    if (!names) return Object.fromEntries(params);

    if (typeof names === 'string') return params.get(names);

    return names.map((name) => params.get(name));
  });

  useLayoutEffect(() => {
    reset(Object.fromEntries(new URLSearchParams(location.search)));
  }, [location.search, reset]);

  const methods = useState(() => ({ setParams, getParams }))[0];

  return <Context.Provider value={methods}>{children}</Context.Provider>;
}

function _set(params, key, value) {
  value == null || (typeof value === 'string' && !value.trim())
    ? params.delete(key)
    : params.set(key, value);
}
